import { ThemeEvents } from '@theme/events';

/**
 * Keeps standalone Shop Pay Installments blocks in sync with variant changes.
 */
class ProductInstallments extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updateInstallments);
    document.addEventListener(ThemeEvents.variantUpdate, this.updateInstallments);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateInstallments);
    document.removeEventListener(ThemeEvents.variantUpdate, this.updateInstallments);
  }

  /**
   * Updates the payment terms form from freshly rendered variant HTML.
   * @param {import('@theme/events').VariantUpdateEvent} event - The variant update event.
   */
  updateInstallments = (event) => {
    if (this.lastHandledVariantUpdateEvent === event) return;
    this.lastHandledVariantUpdateEvent = event;

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newInstallments = [...event.detail.data.html.querySelectorAll('product-installments')].find(
      (installments) => installments instanceof HTMLElement && installments.dataset.blockId === this.dataset.blockId
    );
    const newPaymentTerms = newInstallments?.querySelector('[ref="paymentTerms"]');
    const currentPaymentTerms = this.querySelector('[ref="paymentTerms"]');
    const selectedVariantId = String(event.detail.resource?.id || '');

    if (!newPaymentTerms || !currentPaymentTerms) {
      this.syncPaymentTermsVariantId(selectedVariantId);
      return;
    }

    currentPaymentTerms.replaceWith(newPaymentTerms);
    this.syncPaymentTermsVariantId(selectedVariantId);
  };

  /**
   * Updates existing payment terms form/component attributes when replacement markup is unavailable.
   * @param {string} selectedVariantId - The selected variant id.
   */
  syncPaymentTermsVariantId(selectedVariantId) {
    if (!selectedVariantId) return;

    const inputs = this.querySelectorAll('input[name="id"]');
    const paymentTermsElements = this.querySelectorAll('shopify-payment-terms, .shopify-payment-terms');

    inputs.forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.value = selectedVariantId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    paymentTermsElements.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.setAttribute('variant-id', selectedVariantId);
        element.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }
}

if (!customElements.get('product-installments')) {
  customElements.define('product-installments', ProductInstallments);
}
